import type { IAccountDataResponse, IGroup } from '@hit-game/shared/types';
import { Plus, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { PageHeader } from '../../components/account/PageHeader';
import { DataStateMessage } from '../../components/common/DataStateMessage';
import { DataToolbar } from '../../components/common/DataToolbar';
import { Pagination } from '../../components/common/Pagination';
import { GroupConfigModal } from '../../components/group/GroupConfigModal';
import { GroupCreateModal } from '../../components/group/GroupCreateModal';
import { GroupPanel } from '../../components/group/GroupPanel';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { useMutation } from '../../hooks/useMutation';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import { fetchAllPages } from '../../lib/http';
import { accountService } from '../../services/account.service';
import { groupService } from '../../services/group.service';

/** Trang cấu hình nhóm: tạo/sửa/xoá + xếp thành viên. Chạy hay dừng là ở Sảnh. */
export function GroupPage() {
  const toast = useToast();
  const confirm = useConfirm();

  const fetcher = useCallback(
    (params: { page: number; limit: number; search?: string }) => groupService.list(params),
    [],
  );
  const list = usePaginatedList<IGroup>(fetcher, { limit: 8 });

  /**
   * Kho tài khoản ở shape SẢNH — bảng nhóm hiện cả trạng thái chạy lẫn proxy của
   * từng thành viên, hai thứ không có trong `accountService.list`.
   */
  const [accounts, setAccounts] = useState<IAccountDataResponse[]>([]);

  /**
   * MỌI nhóm, không phải trang đang xem — chỉ để biết tài khoản nào đã có chủ.
   * Lấy theo trang thì tài khoản nằm ở nhóm trang 2 vẫn được mời thêm vào nhóm
   * trang 1, và thế là nó đánh cho hai nhóm cùng lúc.
   */
  const [allGroups, setAllGroups] = useState<IGroup[]>([]);

  const [creating, setCreating] = useState(false);
  /** Nhóm đang mở hộp thoại sửa cấu hình (tên, game, phe + mệnh giá). */
  const [editing, setEditing] = useState<IGroup | null>(null);

  const loadPool = useCallback(() => {
    const load = async () => {
      try {
        const [accountList, groupList] = await Promise.all([
          accountService.overview(),
          fetchAllPages((params) => groupService.list(params)),
        ]);
        setAccounts(accountList);
        setAllGroups(groupList);
      } catch {
        setAccounts([]);
        setAllGroups([]);
      }
    };
    void load();
  }, []);

  useEffect(loadPool, [loadPool]);

  /**
   * Mọi thao tác ghi đều đụng CẢ hai: danh sách nhóm đang xem và kho tài khoản
   * (cấu hình cược của tài khoản lấy từ nhóm nó thuộc về, và tài khoản vừa bị
   * gỡ khỏi nhóm thì rảnh trở lại).
   */
  const mutate = useMutation(() => {
    list.reload();
    loadPool();
  });

  // `GroupCreateModal` đã bảo đảm có đủ tên và game, khỏi phải chống `undefined`.
  // Tạo xong DỪNG ở đây: nhóm rỗng hiện ngay trong danh sách với bảng thành viên
  // trống, thêm người và đặt mệnh giá làm thẳng trên đó khi nào muốn.
  const create = async (input: Pick<IGroup, 'name' | 'gameType'>): Promise<boolean> => {
    const ok = await mutate(() => groupService.create(input));
    if (ok) toast.success('Đã tạo nhóm');
    return ok;
  };

  const save = async (group: IGroup, input: Partial<IGroup>): Promise<boolean> => {
    const ok = await mutate(() => groupService.update(String(group._id), input));
    if (ok) toast.success('Đã lưu nhóm');
    return ok;
  };

  const remove = async (group: IGroup) => {
    const accepted = await confirm({
      title: 'Xoá nhóm',
      message: `Xoá nhóm "${group.name}"? Tài khoản trong nhóm KHÔNG bị xoá, chỉ trở về danh sách chưa phân nhóm.`,
      confirmText: 'Xoá',
      type: 'danger',
    });
    if (!accepted) return;

    if (await mutate(() => groupService.remove(String(group._id)))) {
      toast.success('Đã xoá nhóm');
    } else {
      toast.error('Không xoá được nhóm');
    }
  };

  const isEmpty = !list.loading && list.items.length === 0;

  return (
    <div className="text-ink space-y-6">
      <PageHeader
        icon={Users}
        title="Nhóm"
        subtitle="Ghép tài khoản thành cặp đối ứng và đặt mệnh giá cược"
      />

      <div className="border-line bg-panel overflow-hidden rounded-xl border">
        <DataToolbar
          icon={<Users size={16} />}
          title="Danh sách nhóm"
          count={list.total}
          searchValue={list.search}
          onSearchChange={list.setSearch}
          searchPlaceholder="Tìm theo tên nhóm..."
          action={
            <button
              type="button"
              onClick={() => {
                setCreating(true);
              }}
              className="bg-accent hover:bg-accent-hover ml-2 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-bold text-white transition-colors"
            >
              <Plus size={14} />
              Tạo nhóm
            </button>
          }
        />

        <DataStateMessage
          loading={list.loading}
          empty={isEmpty ? 'Chưa có nhóm nào' : null}
          emptyHint={isEmpty ? 'Bấm "Tạo nhóm" để ghép cặp tài khoản CHẴN / LẺ' : undefined}
          emptyIcon={<Users size={32} className="text-line" />}
        />

        {!list.loading && list.items.length > 0 && (
          // Nền chìm hơn thẻ nhóm — không thì thẻ `bg-panel` chồng lên nền
          // `bg-panel` của cả khối, chỉ còn mỗi đường viền phân tách.
          <div className="bg-canvas space-y-4 p-5">
            {list.items.map((group) => (
              <GroupPanel
                key={String(group._id)}
                group={group}
                accounts={accounts}
                allGroups={allGroups}
                onSave={save}
                onEditConfig={setEditing}
                onDelete={(item) => {
                  void remove(item);
                }}
              />
            ))}
          </div>
        )}

        <Pagination
          page={list.page}
          totalPages={list.totalPages}
          onPageChange={list.setPage}
          loading={list.loading}
        />
      </div>

      <GroupCreateModal
        open={creating}
        onClose={() => {
          setCreating(false);
        }}
        onSubmit={create}
      />

      <GroupConfigModal
        group={editing}
        accounts={accounts}
        onClose={() => {
          setEditing(null);
        }}
        onSubmit={save}
      />
    </div>
  );
}
